import { Input } from '@/components'
import { Patterns } from '@/utils/helper'
import { Visibility, VisibilityOff, Close, Lock } from '@mui/icons-material'
import { Box, Modal, Typography, useMediaQuery, useTheme, IconButton, styled } from '@mui/material'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { LoadingButton } from '@mui/lab'
import SVG from 'react-inlinesvg'
import { SVGS } from '@/assets/svgs'
import { Auth } from 'aws-amplify'
import toast from 'react-hot-toast'

interface ChangePasswordModalProps {
  open: boolean
  onClose: () => void
}

// ==================== STYLED COMPONENTS ====================
const ModalOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(8px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1300,
  padding: '24px',
})

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: '520px',
  backgroundColor: '#ffffff',
  borderRadius: '20px',
  boxShadow: `
    0 20px 60px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(226, 232, 240, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 1)
  `,
  overflow: 'hidden',
  outline: 'none',

  [theme.breakpoints.down('sm')]: {
    maxWidth: '95%',
    margin: '0 16px',
  },
}))

const UpdateButton = styled(LoadingButton)(({ theme }) => ({
  height: '52px',
  borderRadius: '12px',
  fontSize: '15px',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
  color: '#ffffff',
  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    background: 'linear-gradient(135deg, #5558e3 0%, #7c4de8 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
  },

  '&:active': {
    transform: 'translateY(0)',
  },

  '&:disabled': {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.5) 0%, rgba(139, 92, 246, 0.5) 100%)',
    color: 'rgba(255, 255, 255, 0.7)',
  },
}))

const ChangePasswordModal = ({ open, onClose }: ChangePasswordModalProps) => {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const theme = useTheme()
  const isLessThanMedium = useMediaQuery(theme.breakpoints.down('md'))

  interface PasswordForm {
    current: string
    new: string
    confirm: string
  }

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm<PasswordForm>()

  const onClickUpdate = async (data: PasswordForm) => {
    if (changingPassword) return
    try {
      setChangingPassword(true)
      const user = await Auth.currentAuthenticatedUser()
      const res = await toast.promise(Auth.changePassword(user, data.current, data.new), {
        loading: 'Changing password...',
        success: <Typography variant='p'>Password has been updated.</Typography>,
        error: (
          <Typography variant='p'>
            Wrong current password or failed to update your password.
          </Typography>
        ),
      })
      if (res === 'SUCCESS') {
        reset({ current: '', new: '', confirm: '' })
        onClose()
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      toast.error(err.toString())
    } finally {
      setChangingPassword(false)
    }
  }

  if (!open) return null

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={(e) => e.stopPropagation()}>
          {/* Close Button */}
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              width: 40,
              height: 40,
              backgroundColor: '#f8fafc',
              border: '2px solid #e2e8f0',
              borderRadius: '12px',
              zIndex: 10,
              transition: 'all 0.2s',
              '&:hover': {
                backgroundColor: '#fee2e2',
                borderColor: '#fca5a5',
                transform: 'rotate(90deg)',
                '& svg': {
                  color: '#ef4444',
                },
              },
            }}
          >
            <Close sx={{ fontSize: 20, color: '#64748b' }} />
          </IconButton>

          {/* Content */}
          <Box
            sx={{
              padding: { xs: '40px 24px 32px', sm: '48px 40px 40px' },
            }}
          >
            {/* Header with Icon */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 4,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 24px rgba(99, 102, 241, 0.25)',
                }}
              >
                <Lock sx={{ fontSize: 28, color: '#ffffff' }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: '28px',
                    fontWeight: 700,
                    color: '#0f172a',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Change Password
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#64748b',
                    mt: 0.5,
                  }}
                >
                  Keep your account secure
                </Typography>
              </Box>
            </Box>

            {/* Form */}
            <Box
              component='form'
              onSubmit={handleSubmit(onClickUpdate)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}
            >
              <Controller
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value}
                    placeholder='Current password*'
                    onChange={field.onChange}
                    rightIcon={
                      <SVG
                        src={showCurrentPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon}
                      />
                    }
                    onToggleIcon={() => setShowCurrentPassword(!showCurrentPassword)}
                    type={showCurrentPassword ? 'text' : 'password'}
                    errorText={errors.current?.message}
                    isError={!!errors.current}
                    name='old-password'
                  />
                )}
                name='current'
                rules={{
                  required: 'Current password is required',
                  pattern: {
                    value: Patterns.password,
                    message:
                      'Password must contain 8+ characters, uppercase, lowercase, number and special character',
                  },
                }}
              />

              <Controller
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value}
                    placeholder='New password*'
                    onChange={field.onChange}
                    rightIcon={
                      <SVG src={showNewPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon} />
                    }
                    onToggleIcon={() => setShowNewPassword(!showNewPassword)}
                    type={showNewPassword ? 'text' : 'password'}
                    errorText={errors.new?.message}
                    isError={!!errors.new}
                    name='new-password'
                  />
                )}
                name='new'
                rules={{
                  required: 'New password is required',
                  pattern: {
                    value: Patterns.password,
                    message:
                      'Password must contain 8+ characters, uppercase, lowercase, number and special character',
                  },
                }}
              />

              <Controller
                control={control}
                render={({ field }) => (
                  <Input
                    value={field.value}
                    placeholder='Confirm password*'
                    onChange={field.onChange}
                    rightIcon={
                      <SVG
                        src={showConfirmPassword ? SVGS.VisibilityOffIcon : SVGS.VisibilityIcon}
                      />
                    }
                    onToggleIcon={() => setShowConfirmPassword(!showConfirmPassword)}
                    type={showConfirmPassword ? 'text' : 'password'}
                    errorText={'Passwords do not match'}
                    isError={!!errors.confirm}
                    name='confirm-password'
                  />
                )}
                name='confirm'
                rules={{
                  validate: (value: string) => value === watch('new'),
                }}
              />

              <UpdateButton
                fullWidth
                type='submit'
                onClick={handleSubmit(onClickUpdate)}
                loading={changingPassword}
                disabled={changingPassword}
              >
                Update Password
              </UpdateButton>
            </Box>
          </Box>
        </ModalContainer>
      </ModalOverlay>
    </Modal>
  )
}

export default ChangePasswordModal
